import { createContext, useCallback, useEffect, useState } from 'react';
import { apiPost, apiPostEmpty, setUnauthorizedHandler, UnauthorizedError } from '../api/client';
import { initiateLogin } from './oauth';

export interface AuthUser {
  /** ID пользователя (из userId или id в ответе /api/identity/get). */
  userId: string;
  companyId?: string | null;
  givenName?: string;
  familyName?: string;
  /** true, если scope содержит company:admin */
  isCompanyAdmin?: boolean;
  /** Эффективная роль в миниаппе (строка из /api/identity/get). */
  miniappRole?: string;
}

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'unavailable';

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  login: (returnUrl?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Обновляет auth-состояние (вызов /api/identity/get). Используется после OAuth callback. */
  refreshAuth: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);

  const login = useCallback(async (returnUrl?: string) => {
    await initiateLogin(returnUrl ?? window.location.pathname + window.location.search);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiPostEmpty('/api/oauth/logout');
    } catch {
      // logout best-effort: даже при ошибке сбрасываем состояние
    }
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      const data = await apiPost<AuthUser & { id?: string }>('/api/identity/get', {});
      setUser({
        ...data,
        userId: data.userId ?? data.id ?? '',
      });
      setStatus('authenticated');
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setUser(null);
        setStatus('unauthenticated');
        return;
      }
      setStatus('unavailable');
    }
  }, []);

  useEffect(() => {
    // Регистрируем обработчик 401 — при получении 401 на любой запрос инициируем логин
    setUnauthorizedHandler(() => {
      setUser(null);
      setStatus('unauthenticated');
    });

    apiPost<AuthUser & { id?: string }>('/api/identity/get', {})
      .then((data) => {
        setUser({
          ...data,
          userId: data.userId ?? data.id ?? '',
        });
        setStatus('authenticated');
      })
      .catch((err) => {
        if (err instanceof UnauthorizedError) {
          setStatus('unauthenticated');
        } else {
          setStatus('unavailable');
        }
      });
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, login, logout, refreshAuth }}>{children}</AuthContext.Provider>
  );
}
